import React, { useState, useRef, useEffect } from "react";
import { useApp } from "../context/AppContext";
import { useAuth } from "../context/AuthContext";
import { useFetch, NeedTenant } from "../hooks/useData";
import { GlassCard, Loading, SectionTitle, Badge, fmtNum } from "../components/common";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import client from "../lib/api";
import {
  Database, DatabaseZap, UploadCloud, Sparkles, CheckCircle2, Loader2, Trash2, Server,
  FileSpreadsheet, Plus, Plug, Settings2, PlugZap, MessageSquareText, Wand2, XCircle, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const DOMAINS = ["Purchase-to-Pay", "Order-to-Cash", "Incident Management", "Patient Flow", "Generic"];
const ALGORITHMS = ["Alpha Miner", "Heuristic Miner", "Inductive Miner", "Split Miner"];
const ASSESSMENTS = ["Discovery", "Conformance", "Performance", "Variants", "Root-Cause"];

function DatasetsTab() {
  const { can } = useAuth();
  const { loadDatasets } = useApp();
  const { data, loading, reload } = useFetch("/datasets");
  if (loading) return <Loading />;
  const del = async (id) => { try { await client.delete(`/datasets/${id}`); await reload(); await loadDatasets(); toast.success("Dataset deleted"); } catch { toast.error("Failed"); } };
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
      {(data || []).map((d) => (
        <GlassCard key={d.id} hover className="p-5" data-testid={`dataset-card-${d.id}`}>
          <div className="flex items-start justify-between">
            <div className="w-10 h-10 rounded-xl pm-gradient flex items-center justify-center"><Database className="w-5 h-5 text-white" /></div>
            <Badge value={d.status} />
          </div>
          <h3 className="font-bold text-slate-900 mt-3">{d.name}</h3>
          <p className="text-xs text-slate-500">{d.domain} · {d.source_system}</p>
          <div className="flex gap-4 mt-3 text-sm">
            <div><span className="font-bold text-slate-900">{fmtNum(d.case_count)}</span> <span className="text-slate-400 text-xs">cases</span></div>
            <div><span className="font-bold text-slate-900">{fmtNum(d.event_count)}</span> <span className="text-slate-400 text-xs">events</span></div>
          </div>
          {can("analyst") && <Button onClick={() => del(d.id)} data-testid={`del-dataset-${d.id}`} variant="ghost" size="sm" className="mt-3 text-red-500 hover:bg-red-50 rounded-lg px-2"><Trash2 className="w-3.5 h-3.5 mr-1" />Delete</Button>}
        </GlassCard>
      ))}
    </div>
  );
}

function FieldInput({ field, value, onChange }) {
  const common = "mt-1 bg-white/70 rounded-xl";
  if (field.type === "textarea") return <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} data-testid={`cfg-${field.key}`} className={`${common} font-mono text-xs`} />;
  if (field.type === "select") return (
    <Select value={value || field.default || field.options[0]} onValueChange={onChange}><SelectTrigger className={common} data-testid={`cfg-${field.key}`}><SelectValue /></SelectTrigger><SelectContent>{field.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select>
  );
  return <Input type={field.type === "password" ? "password" : "text"} value={value || field.default || ""} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} data-testid={`cfg-${field.key}`} className={common} />;
}

function ConnectorStudio({ connector, templates, onClose, onSynced }) {
  const tpl = templates[connector.type] || { fields: [], real: false };
  const [config, setConfig] = useState(connector.config || {});
  const [mapping, setMapping] = useState(connector.mapping || {});
  const [algorithm, setAlgorithm] = useState(connector.algorithm || "Inductive Miner");
  const [assessment, setAssessment] = useState(connector.assessment_profile || ["Discovery", "Conformance", "Performance"]);
  const [testResult, setTestResult] = useState(null);
  const [busy, setBusy] = useState("");

  const test = async () => {
    setBusy("test"); setTestResult(null);
    try { const { data } = await client.post(`/connectors/${connector.id}/test`, { config }); setTestResult(data); data.ok ? toast.success(data.real ? "Live connection OK" : "Handshake OK (simulated)") : toast.error("Connection failed"); }
    catch (e) { setTestResult({ ok: false, error: e.response?.data?.detail || "Failed", columns: [], sample: [] }); toast.error("Connection failed"); }
    finally { setBusy(""); }
  };
  const save = async () => {
    setBusy("save");
    try { await client.put(`/connectors/${connector.id}/config`, { config, mapping, algorithm, assessment_profile: assessment }); toast.success("Configuration saved"); }
    catch { toast.error("Save failed"); } finally { setBusy(""); }
  };
  const sync = async () => {
    setBusy("sync");
    try { await client.put(`/connectors/${connector.id}/config`, { config, mapping, algorithm, assessment_profile: assessment });
      const { data } = await client.post(`/connectors/${connector.id}/sync`); toast.success(`Synced ${data.event_count} events → "${data.name}"`); onSynced?.(); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || "Sync failed"); } finally { setBusy(""); }
  };

  const cols = testResult?.columns || [];
  const mapField = (key, label) => (
    <div>
      <label className="text-xs font-medium text-slate-500">{label}</label>
      <Select value={mapping[key] || ""} onValueChange={(v) => setMapping({ ...mapping, [key]: v === "none" ? null : v })}>
        <SelectTrigger className="mt-1 bg-white/70 rounded-xl" data-testid={`map-${key}`}><SelectValue placeholder={cols.length ? "Select column" : "Test connection first"} /></SelectTrigger>
        <SelectContent>{key === "resource_column" && <SelectItem value="none">None</SelectItem>}{cols.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="glass-strong max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Plug className="w-5 h-5 text-cyan-600" />{connector.name}
          <span className="text-xs font-normal text-slate-400 ml-1">{tpl.real ? "Live adapter" : "Template · simulated"}</span></DialogTitle></DialogHeader>
        <Tabs defaultValue="config" className="mt-2">
          <TabsList className="glass rounded-xl p-1">
            <TabsTrigger value="config" data-testid="studio-config" className="rounded-lg data-[state=active]:pm-gradient data-[state=active]:text-white"><Settings2 className="w-4 h-4 mr-1.5" />Configure</TabsTrigger>
            <TabsTrigger value="test" data-testid="studio-test" className="rounded-lg data-[state=active]:pm-gradient data-[state=active]:text-white"><PlugZap className="w-4 h-4 mr-1.5" />Test</TabsTrigger>
            <TabsTrigger value="mapping" data-testid="studio-mapping" className="rounded-lg data-[state=active]:pm-gradient data-[state=active]:text-white"><Wand2 className="w-4 h-4 mr-1.5" />Mapping</TabsTrigger>
          </TabsList>

          <TabsContent value="config" className="mt-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              {tpl.fields.map((f) => (
                <div key={f.key} className={f.type === "textarea" ? "col-span-2" : ""}>
                  <label className="text-xs font-medium text-slate-500">{f.label}{f.required && <span className="text-red-400"> *</span>}</label>
                  <FieldInput field={f} value={config[f.key]} onChange={(v) => setConfig({ ...config, [f.key]: v })} />
                </div>
              ))}
            </div>
            <div className="flex gap-2 pt-1">
              <Button onClick={test} disabled={busy === "test"} data-testid="test-connection" className="rounded-xl pm-gradient text-white border-0">{busy === "test" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><PlugZap className="w-4 h-4 mr-1.5" />Test connection</>}</Button>
              <Button onClick={save} disabled={busy === "save"} variant="outline" className="rounded-xl">Save config</Button>
            </div>
          </TabsContent>

          <TabsContent value="test" className="mt-4">
            {!testResult ? <p className="text-sm text-slate-400 py-8 text-center">Run "Test connection" from the Configure tab to preview staged source records.</p> : testResult.ok ? (
              <div>
                <div className="flex items-center gap-2 text-emerald-600 text-sm font-semibold mb-3"><CheckCircle2 className="w-4 h-4" />{testResult.real ? "Live source reachable" : "Simulated handshake OK"}{testResult.note && <span className="font-normal text-slate-400">· {testResult.note}</span>}</div>
                <div className="rounded-xl border border-slate-200 overflow-x-auto">
                  <table className="w-full text-xs"><thead><tr className="bg-slate-50">{cols.map((c) => <th key={c} className="text-left p-2 font-semibold text-slate-500 whitespace-nowrap">{c}</th>)}</tr></thead>
                    <tbody>{testResult.sample.map((r, i) => <tr key={i} className="border-t border-slate-100">{cols.map((c) => <td key={c} className="p-2 text-slate-600 whitespace-nowrap">{String(r[c]).slice(0, 28)}</td>)}</tr>)}</tbody></table>
                </div>
              </div>
            ) : <div className="flex items-start gap-2 text-red-600 text-sm p-4 rounded-xl bg-red-50 border border-red-200"><XCircle className="w-4 h-4 mt-0.5" /><div><b>Connection failed.</b><br /><span className="text-red-500">{testResult.error}</span></div></div>}
          </TabsContent>

          <TabsContent value="mapping" className="mt-4 space-y-4">
            <p className="text-xs text-slate-500">Map staged source fields to the ProClytic Canonical Event Format (PMCEF).</p>
            <div className="grid grid-cols-2 gap-3">
              {mapField("case_id_column", "Case ID")}{mapField("activity_column", "Activity")}
              {mapField("timestamp_column", "Timestamp")}{mapField("resource_column", "Resource")}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-slate-500">Mining algorithm</label>
                <Select value={algorithm} onValueChange={setAlgorithm}><SelectTrigger className="mt-1 bg-white/70 rounded-xl" data-testid="map-algorithm"><SelectValue /></SelectTrigger><SelectContent>{ALGORITHMS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><label className="text-xs font-medium text-slate-500">Assessment profile</label>
                <div className="flex flex-wrap gap-1.5 mt-1.5">{ASSESSMENTS.map((a) => {
                  const on = assessment.includes(a);
                  return <button key={a} onClick={() => setAssessment(on ? assessment.filter((x) => x !== a) : [...assessment, a])} data-testid={`assess-${a}`}
                    className={`px-2.5 py-1 rounded-lg text-xs font-medium pm-transition ${on ? "pm-gradient text-white" : "bg-white/60 border border-slate-200 text-slate-500"}`}>{a}</button>;
                })}</div>
              </div>
            </div>
            <div className="flex gap-2 pt-1">
              <Button onClick={sync} disabled={busy === "sync"} data-testid="sync-connector" className="rounded-xl pm-gradient text-white border-0">{busy === "sync" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-4 h-4 mr-1.5" />Sync & mine</>}</Button>
              <Button onClick={save} disabled={busy === "save"} variant="outline" className="rounded-xl">Save mapping</Button>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function ConnectorsTab() {
  const { can } = useAuth();
  const { loadDatasets } = useApp();
  const { data, loading, reload } = useFetch("/connectors");
  const { data: templates } = useFetch("/connectors/templates");
  const [form, setForm] = useState({ name: "", type: "generic_rest" });
  const [adding, setAdding] = useState(false);
  const [studio, setStudio] = useState(null);
  if (loading || !templates) return <Loading />;

  const add = async () => {
    if (!form.name) return;
    const tpl = templates[form.type];
    try { const { data: c } = await client.post("/connectors", { name: form.name, type: form.type, category: tpl?.category || "Custom" }); await reload(); setForm({ name: "", type: "generic_rest" }); setAdding(false); setStudio(c); }
    catch { toast.error("Failed"); }
  };
  const del = async (id) => { try { await client.delete(`/connectors/${id}`); await reload(); } catch { toast.error("Failed"); } };

  return (
    <div className="space-y-4">
      {studio && <ConnectorStudio connector={studio} templates={templates} onClose={() => { setStudio(null); reload(); }} onSynced={loadDatasets} />}
      {can("connector_admin") && !adding && <div className="flex justify-end"><Button onClick={() => setAdding(true)} data-testid="add-connector-btn" className="rounded-xl pm-gradient text-white border-0"><Plus className="w-4 h-4 mr-1" />Add connector</Button></div>}
      {adding && (
        <GlassCard className="p-5 flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]"><label className="text-xs text-slate-500">Name</label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="connector-name" placeholder="e.g. Prod ERP" className="mt-1 bg-white/70 rounded-xl" /></div>
          <div className="min-w-[200px]"><label className="text-xs text-slate-500">System type</label>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}><SelectTrigger className="mt-1 bg-white/70 rounded-xl" data-testid="connector-type"><SelectValue /></SelectTrigger><SelectContent>{Object.entries(templates).map(([k, t]) => <SelectItem key={k} value={k}>{t.label} {t.real ? "· live" : "· template"}</SelectItem>)}</SelectContent></Select>
          </div>
          <Button onClick={add} data-testid="save-connector" className="rounded-xl pm-gradient text-white border-0">Create & configure</Button>
          <Button onClick={() => setAdding(false)} variant="ghost" className="rounded-xl">Cancel</Button>
        </GlassCard>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {(data || []).map((c) => (
          <GlassCard key={c.id} hover className="p-5" data-testid={`connector-card-${c.id}`}>
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-600 to-slate-800 flex items-center justify-center"><Server className="w-5 h-5 text-white" /></div>
              <Badge value={c.status} />
            </div>
            <h3 className="font-bold text-slate-900 mt-3">{c.name}</h3>
            <p className="text-xs text-slate-500">{(templates[c.type]?.label) || c.category} · {fmtNum(c.records)} records</p>
            {can("connector_admin") && (
              <div className="flex gap-2 mt-3">
                <Button onClick={() => setStudio(c)} data-testid={`configure-${c.id}`} size="sm" className="rounded-lg pm-gradient text-white border-0 flex-1"><Settings2 className="w-3.5 h-3.5 mr-1" />Configure</Button>
                <Button onClick={() => del(c.id)} variant="ghost" size="sm" className="text-red-500 hover:bg-red-50 rounded-lg px-2"><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            )}
          </GlassCard>
        ))}
      </div>
    </div>
  );
}

function IngestTab() {
  const { loadDatasets } = useApp();
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [meta, setMeta] = useState({ name: "", domain: "Generic" });
  const [confirming, setConfirming] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true); setPreview(null);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await client.post("/ingest/upload", fd);
      setPreview(data);
      const inf = data.inferred_schema || {};
      setMapping({ case_id_column: inf.case_id_column || data.columns[0], activity_column: inf.activity_column || data.columns[1],
        timestamp_column: inf.timestamp_column || data.columns[2], resource_column: inf.resource_column || "none" });
      setMeta({ name: file.name.replace(/\.[^.]+$/, ""), domain: inf.detected_domain || "Generic" });
      toast.success("Schema inferred by AI");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };
  const confirm = async () => {
    setConfirming(true);
    try {
      await client.post("/ingest/confirm", { upload_id: preview.upload_id, name: meta.name, domain: meta.domain,
        case_id_column: mapping.case_id_column, activity_column: mapping.activity_column, timestamp_column: mapping.timestamp_column,
        resource_column: mapping.resource_column === "none" ? null : mapping.resource_column });
      toast.success("Dataset ingested & mined"); setPreview(null); await loadDatasets();
    } catch (err) { toast.error(err.response?.data?.detail || "Ingestion failed"); } finally { setConfirming(false); }
  };
  const inf = preview?.inferred_schema; const cols = preview?.columns || [];
  const FIELD = (key, label) => (
    <div><label className="text-xs font-medium text-slate-500">{label}</label>
      <Select value={mapping[key]} onValueChange={(v) => setMapping({ ...mapping, [key]: v })}>
        <SelectTrigger className="mt-1 bg-white/70 rounded-xl" data-testid={`map-${key}`}><SelectValue /></SelectTrigger>
        <SelectContent>{key === "resource_column" && <SelectItem value="none">None</SelectItem>}{cols.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
      </Select></div>
  );
  return (
    <div className="space-y-5">
      {!preview ? (
        <GlassCard className="p-10">
          <div onClick={() => fileRef.current?.click()} data-testid="upload-dropzone" className="border-2 border-dashed border-cyan-300 rounded-2xl p-12 flex flex-col items-center justify-center cursor-pointer hover:bg-cyan-50/40 pm-transition">
            {uploading ? <Loader2 className="w-10 h-10 text-cyan-500 animate-spin mb-3" /> : <div className="w-14 h-14 rounded-2xl pm-gradient flex items-center justify-center mb-4"><UploadCloud className="w-7 h-7 text-white" /></div>}
            <h3 className="font-bold text-slate-800">{uploading ? "Analysing your event log…" : "Drop a CSV or Excel event log"}</h3>
            <p className="text-sm text-slate-500 mt-1">Zero-config ingestion — our AI auto-detects Case ID, Activity, Timestamp & Resource.</p>
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={onFile} data-testid="file-input" />
          </div>
        </GlassCard>
      ) : (
        <GlassCard className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-9 h-9 rounded-xl pm-gradient flex items-center justify-center"><Sparkles className="w-4 h-4 text-white" /></div>
            <div><h3 className="font-bold text-slate-900">Confirm inferred schema</h3><p className="text-xs text-slate-500">{preview.row_count} rows · {cols.length} columns</p></div>
            {inf && <span className="ml-auto text-xs bg-emerald-100 text-emerald-700 font-semibold px-3 py-1 rounded-full">AI confidence {inf.confidence}% · {inf.detected_domain}</span>}
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">{FIELD("case_id_column", "Case ID")}{FIELD("activity_column", "Activity")}{FIELD("timestamp_column", "Timestamp")}{FIELD("resource_column", "Resource")}</div>
          <div className="grid grid-cols-2 gap-3 mb-5">
            <div><label className="text-xs font-medium text-slate-500">Dataset name</label><Input value={meta.name} onChange={(e) => setMeta({ ...meta, name: e.target.value })} data-testid="ingest-name" className="mt-1 bg-white/70 rounded-xl" /></div>
            <div><label className="text-xs font-medium text-slate-500">Domain</label><Select value={meta.domain} onValueChange={(v) => setMeta({ ...meta, domain: v })}><SelectTrigger className="mt-1 bg-white/70 rounded-xl" data-testid="ingest-domain"><SelectValue /></SelectTrigger><SelectContent>{DOMAINS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div className="rounded-xl border border-slate-200 overflow-x-auto mb-5"><table className="w-full text-xs"><thead><tr className="bg-slate-50">{cols.map((c) => <th key={c} className="text-left p-2 font-semibold text-slate-500 whitespace-nowrap">{c}</th>)}</tr></thead><tbody>{preview.sample.slice(0, 5).map((row, i) => <tr key={i} className="border-t border-slate-100">{cols.map((c) => <td key={c} className="p-2 text-slate-600 whitespace-nowrap">{String(row[c]).slice(0, 24)}</td>)}</tr>)}</tbody></table></div>
          <div className="flex gap-3"><Button onClick={confirm} disabled={confirming} data-testid="confirm-ingest" className="rounded-xl pm-gradient text-white border-0 h-11">{confirming ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-2" />Confirm & mine</>}</Button><Button onClick={() => setPreview(null)} variant="ghost" className="rounded-xl h-11">Cancel</Button></div>
        </GlassCard>
      )}
    </div>
  );
}

function UnstructuredTab() {
  const { datasets, loadDatasets } = useApp();
  const [text, setText] = useState("Subject: Order #ORD-5521 escalation\n\nHi team, customer order ORD-5521 was created on 2026-03-02 at 09:14 by Sarah. Credit check completed by the billing system at 11:40. Items were picked by the warehouse bot on 2026-03-03 08:00 but shipping was delayed. Order finally shipped 2026-03-04 15:20 by David, invoice raised same day, payment received 2026-03-10.");
  const [hint, setHint] = useState("Order-to-Cash");
  const [events, setEvents] = useState(null);
  const [target, setTarget] = useState("new");
  const [name, setName] = useState("Email Extract");
  const [busy, setBusy] = useState("");

  const extract = async () => {
    setBusy("extract"); setEvents(null);
    try { const { data } = await client.post("/ingest/unstructured/extract", { text, hint }); setEvents(data.events); data.events.length ? toast.success(`${data.events.length} events extracted`) : toast.message("No process events found"); }
    catch (e) { toast.error(e.response?.data?.detail || "Extraction failed"); } finally { setBusy(""); }
  };
  const save = async () => {
    setBusy("save");
    try { const body = { events, source_label: "Unstructured Text" }; if (target === "new") { body.name = name; body.domain = hint; } else body.dataset_id = target;
      const { data } = await client.post("/ingest/unstructured/confirm", body); toast.success(`${data.events_added} events added`); await loadDatasets(); setEvents(null); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setBusy(""); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      <GlassCard className="p-6 space-y-3">
        <div className="flex items-center gap-2"><div className="w-9 h-9 rounded-xl pm-gradient flex items-center justify-center"><MessageSquareText className="w-4 h-4 text-white" /></div><h3 className="font-bold text-slate-900">Extract from text</h3></div>
        <p className="text-xs text-slate-500">Paste an email, chat log or ticket. The LLM extracts process events into PMCEF.</p>
        <Textarea value={text} onChange={(e) => setText(e.target.value)} data-testid="unstructured-text" className="bg-white/70 rounded-xl min-h-[220px] text-sm" />
        <div className="flex gap-2 items-end">
          <div className="flex-1"><label className="text-xs text-slate-500">Domain hint</label><Select value={hint} onValueChange={setHint}><SelectTrigger className="mt-1 bg-white/70 rounded-xl" data-testid="unstructured-hint"><SelectValue /></SelectTrigger><SelectContent>{DOMAINS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select></div>
          <Button onClick={extract} disabled={busy === "extract"} data-testid="extract-events" className="rounded-xl pm-gradient text-white border-0 h-10">{busy === "extract" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Wand2 className="w-4 h-4 mr-1.5" />Extract</>}</Button>
        </div>
      </GlassCard>
      <GlassCard className="p-6">
        <h3 className="font-bold text-slate-900 mb-3">Extracted events</h3>
        {!events ? <p className="text-sm text-slate-400 py-10 text-center">Extracted process events will appear here for review.</p> : events.length === 0 ? <p className="text-sm text-slate-400 py-10 text-center">No events found in the text.</p> : (
          <div className="space-y-3">
            <div className="max-h-[240px] overflow-y-auto rounded-xl border border-slate-200">
              <table className="w-full text-xs"><thead><tr className="bg-slate-50"><th className="text-left p-2 text-slate-500">Case</th><th className="text-left p-2 text-slate-500">Activity</th><th className="text-left p-2 text-slate-500">Timestamp</th><th className="text-left p-2 text-slate-500">Resource</th></tr></thead>
                <tbody>{events.map((e, i) => <tr key={i} className="border-t border-slate-100" data-testid={`extracted-${i}`}><td className="p-2 font-mono text-slate-600">{e.case_id}</td><td className="p-2 text-slate-700">{e.activity}</td><td className="p-2 text-slate-500">{(e.timestamp || "").slice(0, 16)}</td><td className="p-2 text-slate-500">{e.resource}</td></tr>)}</tbody></table>
            </div>
            <div className="flex gap-2 items-end">
              <div className="flex-1"><label className="text-xs text-slate-500">Target dataset</label>
                <Select value={target} onValueChange={setTarget}><SelectTrigger className="mt-1 bg-white/70 rounded-xl" data-testid="unstructured-target"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="new">New dataset</SelectItem>{datasets.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent></Select>
              </div>
              {target === "new" && <div className="flex-1"><label className="text-xs text-slate-500">Name</label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="unstructured-name" className="mt-1 bg-white/70 rounded-xl" /></div>}
              <Button onClick={save} disabled={busy === "save"} data-testid="save-unstructured" className="rounded-xl pm-gradient text-white border-0 h-10">{busy === "save" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save to lake"}</Button>
            </div>
          </div>
        )}
      </GlassCard>
    </div>
  );
}

function Inner() {
  return (
    <div className="space-y-5 pm-fade-up">
      <SectionTitle eyebrow="Data Layer" title="Data Studio" sub="Datasets, connectors, zero-config ingestion & unstructured fusion" />
      <Tabs defaultValue="datasets">
        <TabsList className="glass rounded-xl p-1 flex-wrap h-auto">
          <TabsTrigger value="datasets" data-testid="tab-datasets" className="rounded-lg data-[state=active]:pm-gradient data-[state=active]:text-white"><Database className="w-4 h-4 mr-1.5" />Datasets</TabsTrigger>
          <TabsTrigger value="connectors" data-testid="tab-connectors" className="rounded-lg data-[state=active]:pm-gradient data-[state=active]:text-white"><DatabaseZap className="w-4 h-4 mr-1.5" />Connectors</TabsTrigger>
          <TabsTrigger value="ingest" data-testid="tab-ingest" className="rounded-lg data-[state=active]:pm-gradient data-[state=active]:text-white"><FileSpreadsheet className="w-4 h-4 mr-1.5" />File Ingest</TabsTrigger>
          <TabsTrigger value="unstructured" data-testid="tab-unstructured" className="rounded-lg data-[state=active]:pm-gradient data-[state=active]:text-white"><MessageSquareText className="w-4 h-4 mr-1.5" />Unstructured</TabsTrigger>
        </TabsList>
        <TabsContent value="datasets" className="mt-5"><DatasetsTab /></TabsContent>
        <TabsContent value="connectors" className="mt-5"><ConnectorsTab /></TabsContent>
        <TabsContent value="ingest" className="mt-5"><IngestTab /></TabsContent>
        <TabsContent value="unstructured" className="mt-5"><UnstructuredTab /></TabsContent>
      </Tabs>
    </div>
  );
}

export default function DataStudio() { return <NeedTenant><Inner /></NeedTenant>; }
